import React, { useEffect, useState } from "react";
import { Chip, Tooltip, CircularProgress } from "@mui/material";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import CustomButtom from "../../../components/buttons/CustomButton";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import Toast from "../../../components/support/Toast";
import { PatientWhatsAppOptOutAPI } from "../../../api/API";

const STOP_MESSAGE = "O paciente deixa de receber mensagens de relacionamento: aniversário, pesquisa de satisfação, pedido de avaliação e retornos. Confirmações e lembretes de consulta continuam, porque fazem parte do atendimento.";
const RESUME_MESSAGE = "O paciente volta a receber as mensagens de relacionamento (aniversário, pesquisa de satisfação, pedido de avaliação e retornos) conforme as automações da clínica.";

function formatDate(value) {
    if (!value) return "";
    const date = new Date(String(value).replace(" ", "T"));
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt-BR");
}

/**
 * Opt-out de mensagens de relacionamento (LGPD L-15) no cabeçalho da ficha.
 * A marca vale para o telefone do paciente; confirmação e lembrete continuam.
 */
function PatientOptOutAction({ patientId }) {
    const [status, setStatus] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", severity: "error" });

    const showToast = (message, severity = "error") => {
        setToast({ open: true, message, severity });
    };

    useEffect(() => {
        PatientWhatsAppOptOutAPI(patientId, "status", (response) => {
            if (response?.status === "SUCCESS") setStatus(response.data);
        });
    }, [patientId]);

    const OnConfirmHandler = () => {
        const action = status?.optedOut ? "revert" : "set";
        setLoading(true);

        PatientWhatsAppOptOutAPI(patientId, action, (response) => {
            setLoading(false);
            if (response?.status !== "SUCCESS") {
                showToast(response?.message || response?.error || "Não foi possível salvar. Tente novamente.");
                return;
            }
            setStatus(response.data);
            setConfirmOpen(false);
            showToast(action === "set"
                ? "Pronto. O paciente não recebe mais mensagens de relacionamento."
                : "Pronto. O paciente volta a receber mensagens de relacionamento.", "success");
        });
    };

    if (!status || !status.hasPhone) return null;

    const optedOut = status.optedOut;
    const since = formatDate(status.since);
    const detail = [since && `desde ${since}`, status.byUserName && `por ${status.byUserName}`].filter(Boolean).join(", ");

    return (
        <>
            {optedOut && (
                <Tooltip title={detail ? `Marcado ${detail}` : ""}>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<NotificationsOffOutlinedIcon />}
                        label="Não recebe mensagens de relacionamento"
                        sx={{ color: "var(--color-text-medium)" }}
                    />
                </Tooltip>
            )}

            <CustomButtom
                style={"primary"}
                variant="outlined"
                size="small"
                label={optedOut ? "Voltar a enviar" : "Parar mensagens de relacionamento"}
                enabled={!loading}
                icon={loading
                    ? <CircularProgress size={18} color="inherit" />
                    : (optedOut ? <NotificationsActiveOutlinedIcon /> : <NotificationsOffOutlinedIcon />)}
                onClick={() => setConfirmOpen(true)}
            />

            <ConfirmDialog
                isOpen={confirmOpen}
                variant={optedOut ? "info" : "warning"}
                title={optedOut ? "Voltar a enviar mensagens de relacionamento" : "Parar mensagens de relacionamento"}
                message={optedOut ? RESUME_MESSAGE : STOP_MESSAGE}
                confirmLabel={optedOut ? "Voltar a enviar" : "Parar mensagens"}
                loading={loading}
                onConfirm={OnConfirmHandler}
                onCancel={() => { if (!loading) setConfirmOpen(false); }}
            />

            <Toast
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={() => setToast((current) => ({ ...current, open: false }))}
            />
        </>
    );
}

export default PatientOptOutAction;
